import React, { useEffect, useState } from 'react'
import { Link } from '@mui/material'
import useSnackbar from '../../data/use-snackbar'
import { useNavigate, useLocation } from 'react-router-dom'
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil'
import {
  activeStepState,
  formDataState,
  loadingState,
  isGlobalDomain,
} from '../helpers'
import Step3 from './step3.component'
import CustomerScan from '../customer/scan.component'
import CustomerInfo from '../customer/info.component'
import M1Stepper from '../layout/stepper.component'
import AppConfig from '../../config'
import ScanSim from '../layout/scansim.component'
import SuccessError from '../layout/successerror.component'
import customerService from '../../services/customer.service'
import simService from '../../services/sim.service'
import SessionTimeoutHandler from '../sessiontimeout.component'

function getSteps() {
  return [
    {
      icon: 1,
      label: 'Scan SIM',
      title: 'Registration',
      subTitle: 'Apply Your Redemption Code / Scan SIM Barcode',
    },
    {
      icon: 2,
      label: 'Register ID',
      title: 'Registration',
      subTitle: 'Register ID',
    },
    { icon: 3, label: 'Upload Documents', title: 'Registration', subTitle: '' },
    {
      icon: 4,
      label: 'Your Information',
      title: 'Registration',
      subTitle: 'Verify Your Information',
    },

    {
      icon: 5,
      label: 'Check & Register',
      title: 'Registration',
      subTitle: 'Check & Register',
    },
  ]
}

function Registration() {
  const [activeStep, setActiveStep] = useRecoilState(activeStepState)
  const steps = getSteps()
  const resetData = useResetRecoilState(formDataState)
  const navigate = useNavigate()
  const location = useLocation()
  const setLoading = useSetRecoilState(loadingState)
  const [formData, setFormData] = useRecoilState(formDataState)
  const [skip, setSkip] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const getStepContent = (step) => {
    switch (step) {
      default:
        return (
          <ScanSim
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
            enabledRedeem={true}
          />
        )
      case 1:
        return (
          <CustomerScan
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
          />
        )
      case 3:
        return (
          <CustomerInfo
            globalCheck={AppConfig.NODE_ENV !== 'development'}
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
            handleCheckEligibility={checkEligibility}
          />
        )
      case 4:
        return (
          <Step3
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
            handleSubmit={handleSubmit}
          />
        )
      case 5:
        return (
          <SuccessError
            canRetry={true}
            handleRetry={handleSubmit}
            onRegisterAnother={handleRegisterAnother}
          />
        )
      case 6:
        return (
          <SuccessError
            canRetry={false}
            handleRetry={handleSubmit}
            onRegisterAnother={handleRegisterAnother}
          />
        )
    }
  }

  const checkEligibility = async () => {
    let result = {
      valid: false,
      errorMsg: '',
    }
    let count = 1

    const postResult = await customerService.eligibility(
      formData.idType,
      formData.idNo,
      formData.nationality,
      1
    )
    if (postResult.Eligibility === 'YES') {
      result.valid = 'YES'
      return result
    }

    if (postResult.Eligibility === 'PENDING') {
      do {
        await new Promise((res) => setTimeout(res, 5000))
        const getResult = await customerService.eligibilityResult(
          postResult.Transaction_Id,
          count === 3
        )
        if (getResult.Eligibility === 'YES') {
          result.valid = 'YES'
        } else if (getResult && getResult.Eligibility === 'FAIL') {
          result.valid = 'FAIL'
          result.errorMsg =
            "Unable to check the customer's eligibility at the moment. Please try again at a later time."
          break
        } else if (getResult && getResult.Eligibility === 'PENDING') {
          if (count === 3) {
            result.valid = 'PENDING'
            result.errorMsg =
              "Unable to check the customer's eligibility at the moment. Please try again at a later time."
            break
          } else {
            count++
          }
        } else {
          result.valid = 'NO'
          result.errorMsg =
            'Customer exceeds the maximum of 3 global prepaid lines. Please de-register one of the prepaid lines and try again.'
          break
        }
      } while (count <= 3 && !result.valid)
    } else if (postResult.Eligibility === 'FAIL') {
      result.valid = 'FAIL'
      result.errorMsg =
        "Unable to check the customer's eligibility at the moment. Please try again at a later time."
    } else {
      result.valid = 'NO'
      result.errorMsg =
        'Customer exceeds the maximum of 3 global prepaid lines. Please de-register one of the prepaid lines and try again.'
    }

    return result
  }

  const handleSubmit = async () => {
    setLoading(true)
    let eligibility = formData.isDataOnlyPlan
    try {
      if (isGlobalDomain && !formData.isDataOnlyPlan) {
        setLoading(false)
        return enqueueSnackbar(
          <>
            This SIM can only be registered in Singapore.
            <Link
              href={AppConfig.SELFREG_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here
            </Link>{' '}
            when you arrive in Singapore to register your SIM.
          </>
        )
      }

      if (!eligibility) {
        let result = await checkEligibility(
          formData.idType,
          formData.idNo,
          formData.nationality,
          1
        )
        if (result.valid === 'YES') {
          eligibility = true
        } else if (result.valid === 'PENDING') {
          setFormData({
            ...formData,
            isSuccess: false,
            errorType: 4,
            errorTitleType: 2,
          })
          setLoading(false)
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        } else if (result.valid === 'FAIL') {
          setFormData({
            ...formData,
            isSuccess: false,
            errorType: 4,
            errorTitleType: 2,
          })
          setLoading(false)
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        } else {
          setFormData({
            ...formData,
            isSuccess: false,
            errorType: 1,
            errorTitleType: 1,
          })
          setLoading(false)
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        }
      }

      if (eligibility) {
        await registerAndProceed()
      }
    } catch (error) {
      setFormData({
        ...formData,
        isSuccess: false,
        errorType: 4,
        errorTitleType: 2,
      })
      setLoading(false)
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const registerAndProceed = async () => {
    try {
      const result = await simService.register(formData)
      setFormData({
        ...formData,
        esim: result?.esim,
        isEsim: result?.isEsim,
        isSuccess: true,
      })
      sessionStorage.clear()
    } catch (error) {
      if (error && error.message && error.message.indexOf('ERR1002') > -1) {
        setFormData({
          ...formData,
          isSuccess: false,
          errorType: -1,
          errorTitleType: 2,
        })
      } else {
        setFormData({
          ...formData,
          isSuccess: false,
          errorType: 2,
          errorTitleType: 2,
        })
      }
    } finally {
      setLoading(false)
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    if (activeStep === 0) navigate('/')
    else if (activeStep === 3)
      setActiveStep((prevActiveStep) => prevActiveStep - 2)
    else setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleRegisterAnother = () => {
    resetData()
    navigate('/')
  }

  useEffect(() => {
    setActiveStep(0)

    return () => {
      setActiveStep(0)
    }
  }, [setActiveStep, resetData])

  useEffect(() => {
    if (activeStep === 0) {
      resetData()
    }
  }, [activeStep, resetData])

  useEffect(() => {
    if (location.state && location.state.code && !skip) {
      setActiveStep(1)
      setSkip(true)
    }
  }, [location.state, setActiveStep, skip])

  return (
    <>
      <SessionTimeoutHandler isSuccessErrorPage={activeStep > 4} />

      {activeStep < 5 && (
        <M1Stepper isStepper={true} activeStep={activeStep} steps={steps} />
      )}

      {getStepContent(activeStep)}
    </>
  )
}

export default Registration
